// Signed-in sessions in Profile → My devices (D212, docs/adding-devices.md).
import type {
  SessionBrowser,
  SessionOs,
  SignedInSession,
} from '@twinden/shared';
import type { useT } from '../i18n';

type Translate = ReturnType<typeof useT>;

// Product names: the same in every language.
const OS_NAMES: Record<SessionOs, string> = {
  iphone: 'iPhone',
  ipad: 'iPad',
  android: 'Android',
  mac: 'Mac',
  windows: 'Windows',
  linux: 'Linux',
  chromeos: 'Chromebook',
};
const BROWSER_NAMES: Record<SessionBrowser, string> = {
  chrome: 'Chrome',
  safari: 'Safari',
  firefox: 'Firefox',
  edge: 'Edge',
  samsung: 'Samsung Internet',
  opera: 'Opera',
};

/**
 * What the session is called (D212): this device's own passkey name when it
 * has one, else the app or the browser and system it runs on.
 */
export function sessionName(session: SignedInSession, t: Translate): string {
  if (session.label) return session.label;
  const os = session.os ? OS_NAMES[session.os] : null;
  if (session.app) {
    return t('account.sessions.appFor', {
      os: os ?? (session.app === 'ios' ? 'iPhone' : 'Android'),
    });
  }
  const browser = session.browser ? BROWSER_NAMES[session.browser] : null;
  if (browser && os) return t('account.sessions.browserOn', { browser, os });
  return browser ?? os ?? t('account.sessions.unknown');
}

/** How it signed in (D212). */
export function sessionHow(session: SignedInSession, t: Translate): string {
  const name = session.passkeyName;
  switch (session.method) {
    case 'passkey':
      return t('account.sessions.via.passkey');
    case 'enrolled':
      return t('account.sessions.via.enrolled');
    case 'passkey_other':
      return name
        ? t('account.sessions.via.passkeyOther', { name })
        : t('account.sessions.via.passkeyOtherUnnamed');
    case 'qr':
      return name
        ? t('account.sessions.via.qr', { name })
        : t('account.sessions.via.qrUnnamed');
    case 'review':
      return t('account.sessions.via.review');
    default:
      return t('account.sessions.via.unknown');
  }
}
